import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useCreateWarrantyClaimsMutation, useDeleteWarrantyClaimsMutation, useUpdateWarrantyClaimsMutation, useWarrantyClaimsApprovementMutation, useWarrantyClaimsReturnMutation, warrantyClaimsApi } from '../../../../../api/endpoints/warrantyClaims';
import toastService from '../../../../../utils/toastService';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { WarrantyClaimStatus } from '../../../../../utils/warrantyClaimsEnums';

const getStoredActiveTabIndex = () => {
    try {
        const savedIndex = localStorage.getItem('warrantyClaimsActiveTab');
        return savedIndex ? parseInt(savedIndex, 10) : 0;
    } catch (error) {
        return 0;
    }
};

// Bu hook detay sayfasının (WarrantyClaimsCreateEdit) ihtiyaç duyduğu save/aksiyon
// mutasyonlarını barındırır. Liste sayfasının filtre/sıralama/dropdown state'i
// `useWarrantyClaimsList` içinde - detay sayfası artık o hook'u import etmiyor.

const useWarrantyClaimsMutations = () => {
    const { th } = useLanguage();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [submitted, setSubmitted] = useState(false);
    const [isRedirecting, setIsRedirecting] = useState(false);

    const [createWarrantyClaims, { isLoading: isCreating }] = useCreateWarrantyClaimsMutation();
    const [updateeWarrantyClaims, { isLoading: isUpdating }] = useUpdateWarrantyClaimsMutation();
    const [deleteWarrantyClaims, { isLoading: isDeleting }] = useDeleteWarrantyClaimsMutation();
    const [approvementWarrantyClaims, { isLoading: isActionLoading }] = useWarrantyClaimsApprovementMutation();
    const [returnWarrantyClaims] = useWarrantyClaimsReturnMutation();

    const invalidateClaimsList = () => {
        dispatch(warrantyClaimsApi.util.invalidateTags(['WarrantyClaims']));
    };

    const saveWarrantyClaims = async (warrantyClaimsData, isEdit, id, status) => {
        try {
            if (isEdit) {
                // Update existing warranty Claims
                await updateeWarrantyClaims({ ...warrantyClaimsData, id: Number(id), status: status ?? getStoredActiveTabIndex() }).unwrap();
                console.log('warranty Claims updated successfully');
                toastService.success(th('warrantyClaims.messages.updateSuccess'), th('common.success'));
                navigate('/warranty-management/warranty-requests/claims');
            } else {
                // Add new warranty Claims
                setIsRedirecting(true);
                const response = await createWarrantyClaims(warrantyClaimsData).unwrap();
                toastService.success(th('warrantyClaims.messages.createSuccess'), th('common.success'));

                // Başarılı response geldiğinde
                if (response?.isSuccessful && response?.data?.id) {
                    // 2 saniye bekleme ekranı göster
                    setTimeout(() => {
                        navigate(`/warranty-management/warranty-requests/claims/${response.data.id}`);
                        setIsRedirecting(false);
                    }, 2000);
                } else {
                    setTimeout(() => {
                        navigate('/warranty-management/warranty-requests/claims');
                        setIsRedirecting(false);
                    }, 2000);
                }
            }
            invalidateClaimsList();
            return true;
        } catch (error) {
            console.error('Error saving warranty Claims:', error);
            setIsRedirecting(false);
            //iKİLİ HATA MESAJI PROBLEMİNDEN DOLAYI ŞİMDİLİK KALDIRILIDI , SORUN ÇIKMAZSA TAMAMEN KALDIRILACAK.
            // toastService.error(isEdit ? th('warrantyClaims.messages.updateError') : th('warrantyClaims.messages.createError'), th('common.error'));
            return false;
        }
    };

    const handleActionSave = async (id, status, alokasyon, desc, isSeePermission) => {
        try {
            if (status === 'delete') {
                await deleteWarrantyClaims(id).unwrap();
                toastService.success(th('warrantyClaims.messages.deleteSuccess'), th('common.success'));
            } else if (status === WarrantyClaimStatus.RETURNED) {
                // Geri gönderme işlemi için ayrı endpoint
                await returnWarrantyClaims({
                    warrantyClaimId: Number(id),
                    isApproved: false,
                    status: status,
                    statusDescription: desc,
                    alokasyon: alokasyon
                }).unwrap();

                toastService.success(th('warrantyClaims.messages.updateSuccess'), th('common.success'));
            } else {
                // Onaylama ve reddetme işlemleri için mevcut endpoint
                const approvementPayload = {
                    warrantyClaimId: Number(id),
                    isApproved: status === WarrantyClaimStatus.ACCEPTED ? true : false,
                    status: status,
                    statusDescription: desc,
                    alokasyon: alokasyon
                };

                if (isSeePermission !== undefined) {
                    approvementPayload.isSeePermission = isSeePermission;
                }

                await approvementWarrantyClaims(approvementPayload).unwrap();

                toastService.success(th('warrantyClaims.messages.updateSuccess'), th('common.success'));
            }
            navigate('/warranty-management/warranty-requests/claims');
            invalidateClaimsList();
            return true;
        } catch (error) {
            console.error('Error saving warranty Claims:', error);
            return false;
        }
    };

    return {
        submitted,
        setSubmitted,
        isCreating,
        isUpdating,
        isDeleting,
        isActionLoading,
        isRedirecting,
        saveWarrantyClaims,
        handleActionSave
    };
};

export default useWarrantyClaimsMutations;
